import React from 'react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { ProfileCard } from '@core/Areas/FollowMe/Components/Common/ProfileCard'
import { EditMyProfile } from '@core/Areas/FollowMe/Components/EditMyProfile'

export const ProfileCreationDisplayTray: React.FC<{
  onCompleteOnboarding(): void
  profileData?: FollowMeProfile
}> = ({ onCompleteOnboarding, profileData }) => {
  const [isProfileCreationTrayOpen, setIsProfileCreationTrayOpen] = React.useState(false)
  const [workflowStatus, setWorkflowStatus] = React.useState<OnboardingWorkflowStatus>('welcome')

  React.useEffect(() => {
    if (!!profileData && profileData.id) setIsProfileCreationTrayOpen(true)
  }, [profileData])

  if (!profileData) return null

  const onEditAvatarClick = () => setWorkflowStatus('edit-avatar')
  const onEditProfileClick = () => setWorkflowStatus('edit-profile')

  const onTrayBackNavigate = workflowStatus === 'edit-avatar' ? onEditProfileClick : undefined

  const onContinueToBetLoopClick = () => {
    setIsProfileCreationTrayOpen(false)
    setTimeout(onCompleteOnboarding, 400)
  }

  const headerTitle =
    workflowStatus === 'welcome'
      ? `Welcome, ${profileData.displayName}`
      : workflowStatus === 'edit-profile'
        ? 'Edit your profile'
        : 'Edit Avatar'

  return createPortal(
    <Tray
      isOpen={isProfileCreationTrayOpen}
      headerTitle={headerTitle}
      onDismiss={onContinueToBetLoopClick}
      onBackNavigate={onTrayBackNavigate}
      footerComponent={
        workflowStatus === 'welcome' ? (
          <>
            <Button
              size='md'
              isFullWidth
              color='secondary_grey'
              onClick={onEditProfileClick}
              data-testid='ProfileCreation.Edit'
            >
              Edit profile
            </Button>

            <Button
              size='md'
              isFullWidth
              color='primary'
              onClick={onContinueToBetLoopClick}
              data-testid='ProfileCreation.Launch'
            >
              Launch Bet Loop
            </Button>
          </>
        ) : undefined
      }
    >
      {workflowStatus === 'welcome' ? (
        <WelcomeWrapperStyled>
          <ProfileCard profile={profileData} />

          <p>
            Your <strong>Profile Card</strong> displays your username, avatar, and key stats - it’s
            how others see you in Bet Loop.
          </p>

          <p>
            We’ve set you up with a random username and avatar. Tap <strong>Edit profile</strong> to
            personalise it now, or change it later from your profile.
          </p>
        </WelcomeWrapperStyled>
      ) : (
        <EditMyProfile
          shouldAutoOpenTray
          handleEditAvatarClick={onEditAvatarClick}
          handleEditProfileClick={onEditProfileClick}
          onSuccessCallback={onContinueToBetLoopClick}
        />
      )}
    </Tray>,
    document.body
  )
}

// ======
// Styles
// ======

const WelcomeWrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.smx1,

  '> div:first-of-type': {
    marginBottom: spacing.big,
  },

  '> p': {
    alignSelf: 'stretch',
    margin: 0,
    fontFamily: font.family.primary,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    fontWeight: font.weight.regular,
    color: colors.neutral[800],
    textAlign: 'unset',
    textWrap: 'pretty',

    '> strong': { fontWeight: font.weight.bold },
  },
})

// =====
// Types
// =====

type OnboardingWorkflowStatus = 'welcome' | 'edit-profile' | 'edit-avatar'
